/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Fragment, ReactNode, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "../../../main";
import { Dialog, Menu, Transition } from "@headlessui/react";
import { toast } from "react-toastify";
import { DeleteBook } from "../../../api/Books/DeleteBook";
import Input from "../../Shared/Input";
import * as Yup from "yup";
import { useFormik } from "formik";
import { AddBookDiscount } from "../../../api/Books/AddBookDiscount";
import { useUserDataStore } from "../../../stores/user";
import { Percent, Pencil, Trash2 } from "lucide-react";
import {
  DashboardActionMenuPanel,
  DashboardMenuButton,
  DashboardMenuLink,
  dashboardActionMenuButtonClass,
  dialogCenterClass,
  dialogOverlayClass,
  dialogPanelClass,
  dialogTextClass,
  dialogTitleClass,
  primaryDialogButtonClass,
} from "../Shared/DashboardActionMenu";

const validationSchema = Yup.object({
  discountPrecentage: Yup.number().required("Discount Precentage is required"),
});

const BookDropdown = ({
  children,
  bookId,
  slug,
}: {
  children?: ReactNode;
  bookId: string;
  slug: string;
}) => {
  const { userData } = useUserDataStore();

  const { mutate: deleteBook } = useMutation({
    mutationFn: DeleteBook,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["books"] });
      toast.success("book deleted successfully");
    },
  });
  const [isOpen, setIsOpen] = useState(false);
  const { mutate } = useMutation({
    mutationFn: AddBookDiscount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["books"] });
      toast.success("Discount Added successfully");
      formik.resetForm();
      setIsOpen(false);
    },
    onError: () => {
      formik.setSubmitting(false);
    },
  });

  const formik = useFormik({
    initialValues: {
      discountPrecentage: "",
    },
    validationSchema,
    onSubmit: (values) => {
      if (bookId) {
        mutate({
          id: bookId,
          payload: {
            ...values,
            discountPrecentage: Number(values.discountPrecentage),
          },
        });
      }
    },
  });

  return (
    <div className="relative ">
      <Menu as="div" className="relative inline-block">
        <div>
          <Menu.Button className={dashboardActionMenuButtonClass}>
            {children}
          </Menu.Button>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <DashboardActionMenuPanel>
            {userData?.role === "admin" && (
              <DashboardMenuLink icon={Pencil} to={`edit-book/${slug}`}>
                Edit Book
              </DashboardMenuLink>
            )}
            <DashboardMenuButton icon={Percent} onClick={() => setIsOpen(true)}>
              Add Discount
            </DashboardMenuButton>
            {userData?.role === "admin" && (
              <DashboardMenuButton danger icon={Trash2} onClick={() => deleteBook(bookId)}>
                Delete Book
              </DashboardMenuButton>
            )}
          </DashboardActionMenuPanel>
        </Transition>
      </Menu>
      <Transition
        enter="ease-out duration-300"
        enterFrom="opacity-0 transform-[scale(95%)]"
        enterTo="opacity-100 transform-[scale(100%)]"
        leave="ease-in duration-200"
        leaveFrom="opacity-100 transform-[scale(100%)]"
        leaveTo="opacity-0 transform-[scale(95%)]"
        appear
        show={isOpen}
      >
        <Dialog
          as="div"
          className="relative z-[9999999999999999999999] focus:outline-none"
          onClose={() => setIsOpen(false)}
        >
          <div className={dialogOverlayClass}>
            <div className={dialogCenterClass}>
              <form
                onSubmit={formik.handleSubmit}
                className={dialogPanelClass}
              >
                <h3 className={dialogTitleClass}>Add Discount</h3>
                <p className={dialogTextClass}>
                  How much do you want your discount to be
                </p>
                <Input
                  value={formik.values.discountPrecentage}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.discountPrecentage &&
                    formik.errors.discountPrecentage
                      ? formik.errors.discountPrecentage
                      : ""
                  }
                  className="w-full"
                  inputStyles=" !h-[50px] !rounded-2xl !border-[#DFEAF2] hover:!border-primary"
                  name="discountPrecentage"
                  placeholder=""
                />

                <div className="mt-5 flex justify-center">
                  <button className={`${primaryDialogButtonClass} w-full sm:w-40`}>
                    Add Discount
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default BookDropdown;
